/**
 * LogoutConfirmModal - Bekräftelsedialog för utloggning
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { LogOut, X } from 'lucide-react';

interface LogoutConfirmModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function LogoutConfirmModal({ isOpen, onConfirm, onCancel }: LogoutConfirmModalProps) {
  // Scroll lock - mer robust implementering
  useEffect(() => {
    if (!isOpen) return;

    // Spara original styles
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    // Beräkna scrollbar bredd
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // Lås scroll
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Cleanup
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Dimmed background */}
      <div 
        className="fixed top-0 left-0 right-0 bottom-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-center justify-center overflow-hidden"
        onClick={onCancel}
        style={{ position: 'fixed', inset: 0 }}
      />
      
      {/* Modal */}
      <div 
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[90vw] max-w-md rounded-xl shadow-2xl overflow-hidden bg-card border border-border"
        style={{ position: 'fixed' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-border bg-card/95 backdrop-blur px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg p-2 bg-orange-500/10">
                <LogOut className="h-5 w-5 text-orange-500" />
              </div>
              <h2 className="text-xl font-bold">Logga ut</h2>
            </div>
            <button
              onClick={onCancel}
              className="rounded-lg p-2 hover:bg-muted transition-colors"
              aria-label="Stäng"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <p className="text-muted-foreground">
            Är du säker på att du vill logga ut från Beredskapshubben?
          </p>
        </div>

        {/* Actions */}
        <div className="border-t border-border bg-muted/30 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors font-medium"
          >
            Avbryt
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white transition-colors font-medium flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            Logga ut
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
